import { z } from "zod";
import { clampedLimit } from "./common.schema";

// ---------------------------------------------------------------------------
// Dedup candidate enums
// ---------------------------------------------------------------------------

export const DedupResolutionActionEnum = z
  .enum(["merge_keep_a", "merge_keep_b", "keep_both", "dismiss"])
  .describe("Resolution Action");
export type DedupResolutionAction = z.infer<typeof DedupResolutionActionEnum>;

export const DedupCandidateStatusEnum = z
  .enum(["pending", "confirmed_duplicate", "confirmed_not_duplicate", "merged"])
  .describe("Status");
export type DedupCandidateStatus = z.infer<typeof DedupCandidateStatusEnum>;

export const DedupMatchFieldEnum = z
  .enum(["email", "phone", "name_fuzzy"])
  .describe("Match Field");
export type DedupMatchField = z.infer<typeof DedupMatchFieldEnum>;

// ---------------------------------------------------------------------------
// Hydrated contact slice for candidate responses. core.contacts is global
// (no business_id), so display fields only.
// ---------------------------------------------------------------------------

export const DedupCandidateContactSchema = z.object({
  id: z.string().uuid().describe("ID"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  email: z.string().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
});
export type DedupCandidateContact = z.infer<typeof DedupCandidateContactSchema>;

// ---------------------------------------------------------------------------
// Candidate row (response shape)
// ---------------------------------------------------------------------------

export const DedupCandidateSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  imported_contact: DedupCandidateContactSchema.describe("Imported Contact"),
  existing_contact: DedupCandidateContactSchema.describe("Existing Contact"),
  confidence: z.number().min(0).max(1).describe("Confidence"),
  match_fields: z.array(DedupMatchFieldEnum).describe("Match Fields"),
  status: DedupCandidateStatusEnum.describe("Status"),
  resolution_action:
    DedupResolutionActionEnum.nullable().describe("Resolution Action"),
  detected_in_import_job_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Detected In Import Job"),
  detected_at: z.string().describe("Detected At"),
  reviewed_by: z.string().uuid().nullable().describe("Reviewed By"),
  reviewed_at: z.string().nullable().describe("Reviewed At"),
});
export type DedupCandidate = z.infer<typeof DedupCandidateSchema>;

// ---------------------------------------------------------------------------
// Request / response wrappers
// ---------------------------------------------------------------------------

export const ListDedupCandidatesQuerySchema = z.object({
  status: DedupCandidateStatusEnum.optional().describe("Status"),
  limit: clampedLimit(200, 50).describe("Limit"),
  offset: z.coerce.number().int().min(0).default(0).describe("Offset"),
});
export type ListDedupCandidatesQuery = z.infer<
  typeof ListDedupCandidatesQuerySchema
>;

export const DedupCandidateListResponseSchema = z.object({
  candidates: z.array(DedupCandidateSchema).describe("Candidates"),
  total: z.number().int().describe("Total"),
  auto_resolvable_count: z.number().int().describe("Auto Resolvable Count"),
});
export type DedupCandidateListResponse = z.infer<
  typeof DedupCandidateListResponseSchema
>;

export const DedupResolutionItemSchema = z.object({
  candidate_id: z.string().uuid().describe("Candidate"),
  action: DedupResolutionActionEnum.describe("Action"),
});
export type DedupResolutionItem = z.infer<typeof DedupResolutionItemSchema>;

export const ResolveDedupCandidatesRequestSchema = z.object({
  resolutions: z
    .array(DedupResolutionItemSchema)
    .min(1, "At least one resolution required")
    .max(500, "Cannot resolve more than 500 candidates in one call")
    .describe("Resolutions"),
});
export type ResolveDedupCandidatesRequest = z.infer<
  typeof ResolveDedupCandidatesRequestSchema
>;

export const ResolveDedupCandidatesResponseSchema = z.object({
  resolved_count: z.number().int().describe("Resolved Count"),
  remaining_count: z.number().int().describe("Remaining Count"),
});
export type ResolveDedupCandidatesResponse = z.infer<
  typeof ResolveDedupCandidatesResponseSchema
>;
